// UsePhotoGalleryData.ts
/**
 * Custom hook for fetching and organizing photo gallery data backed by
 * the unified /gallery endpoint.
 */
import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { SessionContext } from "../../../../contexts";
import {
  TPatientInfo,
  TClinicGalleryItem,
  TClinicGalleryPatientInfo,
  getClinicGallery,
} from "../../../../services/api-clinic";
import { TGetPatientImageData } from "../../../../services/api";
import { YearGroup } from "./PhotoGalleryScreen";
import { useDebounced } from "../../../../utils/useDebounced";

type GallerySearch = {
  query?: string;
  mode?: "auto" | "name" | "email" | "phone";
};

const hashItemKeyToNumber = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const usePhotoGalleryData = (search?: GallerySearch) => {
  const session = useContext(SessionContext)?.session;
  const clinicId = session?.clinicId ?? "";
  const accessToken = session?.accessToken ?? "";

  const [groupedData, setGroupedData] = useState<{ [year: string]: YearGroup }>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track the currently active run; newer runs invalidate older ones.
  const activeRunRef = useRef<string>("");

  // For safe progressive merges without stale closure
  const groupedRef = useRef(groupedData);
  useEffect(() => {
    groupedRef.current = groupedData;
  }, [groupedData]);

  const debouncedQuery = useDebounced(search?.query?.trim() || "", 400);

  // Change this whenever clinic, token, or filter changes
  const runKey = `${clinicId}|${accessToken}|${debouncedQuery}|${search?.mode ?? "auto"}`;

  const guardedMerge = useCallback(
    (incoming: { [year: string]: YearGroup }, runAtStart: string) => {
      // Ignore stale results
      if (activeRunRef.current !== runAtStart) return;

      const next = { ...groupedRef.current };
      for (const [year, yGroup] of Object.entries(incoming)) {
        if (!next[year]) next[year] = { year, months: {} };
        for (const [monthYear, mGroup] of Object.entries(yGroup.months)) {
          if (!next[year].months[monthYear])
            next[year].months[monthYear] = { monthYear, days: {} };
          for (const [dayKey, dGroup] of Object.entries(mGroup.days)) {
            if (!next[year].months[monthYear].days[dayKey]) {
              next[year].months[monthYear].days[dayKey] = {
                date: dayKey,
                patients: {},
              };
            }
            for (const [patientId, pData] of Object.entries(dGroup.patients)) {
              const dest =
                next[year].months[monthYear].days[dayKey].patients[patientId] ??
                (next[year].months[monthYear].days[dayKey].patients[patientId] =
                  {
                    info: pData.info,
                    images: [],
                  });

              const seen = new Set(dest.images.map((img) => img.image_data.id));
              for (const img of pData.images)
                if (!seen.has(img.image_data.id)) dest.images.push(img);
            }
          }
        }
      }
      setGroupedData(next);
    },
    [],
  );

  const normalizePatientInfo = useCallback(
    (raw?: TClinicGalleryPatientInfo | null): TPatientInfo | null => {
      if (!raw) return null;
      return {
        areasOfInterest: raw.areas_of_interest || [],
        createdAt: raw.created_at || "",
        dateOfBirth: raw.dob || "",
        email: raw.email || "",
        firstName: raw.first_name || "",
        lastName: raw.last_name || "",
        phone: raw.phone || "",
        proceduresOfInterest: raw.procedures_of_interest || [],
        sex: raw.sex || "",
        updatedAt: raw.updated_at || "",
        uuid: raw.uuid || "",
        uploadType: raw.upload_type || "",
        location: raw.location || "",
        type: raw.type || "",
      };
    },
    [],
  );

  const createPatientInfoFromItem = useCallback(
    (item: TClinicGalleryItem): TPatientInfo => {
      const normalized = normalizePatientInfo(item.patient_info);

      const fallback: TPatientInfo = {
        areasOfInterest: [],
        createdAt: item.created_at,
        dateOfBirth: "",
        email: item.patient_email ?? "",
        firstName: item.patient_name?.first_name ?? "",
        lastName: item.patient_name?.last_name ?? "",
        phone: item.patient_phone ?? "",
        proceduresOfInterest: [],
        sex: "",
        updatedAt: item.created_at,
        uuid: item.patient_uuid || `unknown-${item.item_key}`,
        uploadType: item.type,
        location: "",
        type: "",
      };

      if (!normalized) return fallback;

      return {
        ...fallback,
        ...normalized,
        email: normalized.email || fallback.email,
        phone: normalized.phone || fallback.phone,
        firstName: normalized.firstName || fallback.firstName,
        lastName: normalized.lastName || fallback.lastName,
        uuid: normalized.uuid || fallback.uuid,
      };
    },
    [normalizePatientInfo],
  );

  const createImageDataFromItem = useCallback(
    (item: TClinicGalleryItem): TGetPatientImageData | null => {
      const thumbCandidate = item.thumbnail_url || "";
      const fullCandidate = item.full_size_url || "";
      if (!thumbCandidate && !fullCandidate) return null;

      const thumbnailUrl = thumbCandidate || fullCandidate;
      const fullUrl = fullCandidate || thumbnailUrl;

      let resolvedId: number | null = null;
      if (typeof item.image_id === "number") {
        resolvedId = item.image_id;
      } else if (typeof item.image_id === "string") {
        const parsed = Number.parseInt(item.image_id, 10);
        resolvedId = Number.isFinite(parsed) ? parsed : null;
      }

      if (resolvedId === null) {
        resolvedId = hashItemKeyToNumber(
          item.item_key || `${item.created_at}-${thumbnailUrl}`,
        );
      }

      return {
        image_data: {
          id: resolvedId,
          procedure_types: [],
          procedure_types_human_readable: [],
          view_type: null,
          view_type_human_readable: null,
          upload_type: item.type,
          uploaded: true,
          days_postop: null,
          surgery_stage: null,
          taken_in_clinic: null,
          is_starred: false,
          external_id: item.asset_id || null,
          tags: [],
          created_at: item.created_at,
          updated_at: item.created_at,
        },
        presigned_urls: fullUrl ? [fullUrl] : [],
        thumbnail_presigned_urls: thumbnailUrl ? [thumbnailUrl] : [],
        edit_target:
          item.type === "regular" && Number.isFinite(resolvedId)
            ? {
                target_type: "clinic_patient_image",
                target_id: resolvedId,
              }
            : null,
      };
    },
    [hashItemKeyToNumber],
  );

  const groupImagesByDate = useCallback(
    (items: TClinicGalleryItem[]) => {
      const grouped: { [year: string]: YearGroup } = {};
      items.forEach((item) => {
        const image = createImageDataFromItem(item);
        if (!image) return;
        const date = new Date(
          item.display_date || item.created_at || Date.now(),
        );
        if (isNaN(date.getTime())) return;
        const year = String(date.getFullYear());
        const monthYear = `${date.toLocaleDateString("en-US", { month: "long" })} ${year}`;
        const dayKey =
          item.grouping_keys?.date || date.toISOString().split("T")[0];
        if (!dayKey) return;

        grouped[year] ??= { year, months: {} };
        grouped[year].months[monthYear] ??= { monthYear, days: {} };
        grouped[year].months[monthYear].days[dayKey] ??= {
          date: dayKey,
          patients: {},
        };

        const pid =
          item.patient_uuid ||
          String(item.patient_id ?? `patient-${item.item_key}`);
        grouped[year].months[monthYear].days[dayKey].patients[pid] ??= {
          info: createPatientInfoFromItem(item),
          images: [],
        };
        grouped[year].months[monthYear].days[dayKey].patients[pid].images.push(
          image,
        );
      });
      return grouped;
    },
    [createImageDataFromItem, createPatientInfoFromItem],
  );

  const filterItemsBySearch = useCallback(
    (items: TClinicGalleryItem[]) => {
      const q = debouncedQuery.trim();
      if (!q) return items;
      const normalized = q.toLowerCase();
      const mode = search?.mode ?? "auto";
      const numericQuery = normalized.replace(/[^\d+]/g, "");
      const isPhonePattern = /^\+?\d[\d\s\-().]{6,}$/;
      const treatAsEmail =
        mode === "email" || (mode === "auto" && normalized.includes("@"));
      const treatAsPhone =
        mode === "phone" ||
        (mode === "auto" && isPhonePattern.test(search?.query ?? ""));

      return items.filter((item) => {
        const displayName =
          item.patient_name?.display_name ||
          `${item.patient_name?.first_name ?? ""} ${item.patient_name?.last_name ?? ""}`;
        const infoDisplay = item.patient_info?.display_name || "";
        const patientInfo = item.patient_info;
        const combinedName = `${displayName} ${infoDisplay}`.trim();
        const email = (
          patientInfo?.email ||
          item.patient_email ||
          ""
        ).toLowerCase();
        const phoneDigits = (
          patientInfo?.phone ||
          item.patient_phone ||
          ""
        ).replace(/[^\d+]/g, "");

        const nameMatch =
          combinedName.toLowerCase().includes(normalized) ||
          (patientInfo?.first_name || "").toLowerCase().includes(normalized) ||
          (patientInfo?.last_name || "").toLowerCase().includes(normalized);
        const emailMatch = email.includes(normalized);
        const phoneMatch =
          numericQuery.length > 0 && phoneDigits.includes(numericQuery);

        if (treatAsEmail) return emailMatch;
        if (treatAsPhone) return phoneMatch;
        return nameMatch || emailMatch || phoneMatch;
      });
    },
    [debouncedQuery, search?.mode, search?.query],
  );

  // Fetch function that is parameterized by the runKey so we can guard results.
  const fetchAllData = useCallback(
    async (runAtStart: string) => {
      if (!session) {
        setError("No session available");
        setLoading(false);
        return;
      }

      activeRunRef.current = runAtStart; // mark this run as the active one
      setLoading(true);
      setError(null);
      setGroupedData({}); // clear prior view immediately
      groupedRef.current = {};

      try {
        const PAGE_LIMIT = 200;
        const visitedCursors = new Set<string>();
        let cursor: string | null | undefined = undefined;

        do {
          if (cursor && visitedCursors.has(cursor)) break;
          if (cursor) visitedCursors.add(cursor);

          const response = await getClinicGallery(session, {
            cursor,
            limit: PAGE_LIMIT,
            mode: "chronological",
            include_patient_info: true,
          });

          if (activeRunRef.current !== runAtStart) return;

          const filtered = filterItemsBySearch(response.items || []);
          if (filtered.length > 0) {
            const grouped = groupImagesByDate(filtered);
            guardedMerge(grouped, runAtStart);
          }

          cursor = response.next_cursor;
        } while (cursor);
      } catch (err) {
        if (activeRunRef.current === runAtStart) {
          console.error("Error fetching gallery data:", err);
          setError(
            err instanceof Error ? err.message : "Unknown error occurred",
          );
        }
      } finally {
        // Only flip loading off if *this* run is still the latest
        if (activeRunRef.current === runAtStart) setLoading(false);
      }
    },
    [session, groupImagesByDate, guardedMerge, filterItemsBySearch],
  );

  // Run exactly once per runKey change
  useEffect(() => {
    fetchAllData(runKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey]);

  const refreshData = useCallback(() => {
    fetchAllData(runKey);
  }, [fetchAllData, runKey]);

  return { groupedData, loading, error, refreshData };
};
